import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { debounceTime, delay, distinctUntilChanged, filter, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { CommonUtilsService } from 'taxilla-library';

import {
    ClearEninvoiceInstancesPagination,
    ClearEninvoiceRequests,
    ClearEninvoiceSearchReports,
    ClearEninvoiceSearchReportsPagination,
    ClearEninvoiceSelectedInstanceReports,
    ClearEninvoiceSelectedInstanceWorkflow,
    ClearInstancesPagination,
    ClearRequests,
    ClearRequestsPagination,
    ClearSearchReports,
    ClearSearchReportsPagination,
    DownloadEninvoiceSearchReport,
    DownloadSearchReport,
    EninvoiceSelectedInstanceGenerateReport,
    ExecuteWorkflowStage,
    GenerateReport,
    GetAppBridgeNodes,
    GetAppDetailsThroughApi,
    GetAppMetaData,
    GetAssetWithLatestVersion,
    GetEninvoiceAppMetaData,
    GetEninvoiceInstance,
    GetEninvoicePackageApps,
    GetEninvoiceRequests,
    GetEninvoiceRequestsByInstanceId,
    GetEninvoiceSearchReports,
    GetEninvoiceSearchReportsPagination,
    GetEninvoiceSelectedInstanceReports,
    GetEninvoiceSelectedInstanceWorkflow,
    GetEninvoiceTransformationChains,
    GetOutboundChains,
    GetPackageApps,
    GetReports,
    GetRequests,
    GetRequestsByInstanceId,
    GetSearchReports,
    GetSearchReportsPagination,
    GetWorkflow,
    SearchEninvoiceInstances,
    SearchInstances,
    SetEninvoiceInstancesPagination,
    SetEninvoiceInstancesSearchCriteria,
    SetEninvoiceRequestsPagination,
    SetEninvoiceSelectedAppDetails,
    SetEninvoiceSelectedInstanceId,
    SetInstancesPagination,
    SetReports,
    SetRequestsPagination,
    SetSearchInstancesCriteria,
    SetWorkflow,
    SubmitEninvoiceWorkflow,
    UpdateEninvoiceSearchReportsPagination,
    UpdateSearchReportsPagination,
} from '../actions';
import { ClearSearchInstances, GetInstance } from '../actions/instances.actions';
import {
    getAssetsWithLatestVersion$,
    getCurrentOrganizationId$,
    getEninvoiceAppMetaData$,
    getEninvoiceInstancesPagination$,
    getEninvoiceRequestsPagination$,
    getEninvoiceSelectedApp$,
    getEninvoiceSelectedAppRestApiName$,
    getEninvoiceSelectedBridgeApp$,
    getEninvoiceSelectedBridgeRestApiName$,
    getEninvoiceSelectedInstance$,
    getEninvoiceSelectedInstanceId$,
    getEninvoiceSelectedPackageApp$,
    getEninvoiceSelectedPackageRestApiName$,
    getEninvoiceSelectedRequestId$,
    getEninvoiceSubscribedAppsApiMap$,
    getSelectedRequestLogName$,
    getSubscribedApps$,
} from '../selectors';

@Injectable()
export class EninvoiceEffects {
    constructor(private actions$: Actions, private store$: Store) {}

    setEninvoiceSelectedAppDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetEninvoiceSelectedInstanceId, SetEninvoiceSelectedAppDetails),
            debounceTime(100),
            withLatestFrom(
                this.store$.select(getEninvoiceSubscribedAppsApiMap$),
                this.store$.select(getEninvoiceSelectedAppRestApiName$),
                this.store$.select(getEninvoiceSelectedPackageRestApiName$),
                this.store$.select(getEninvoiceSelectedBridgeRestApiName$)
            ),
            mergeMap(([_action, appsMap, restApiName, packageRestApiName, bridgeRestApiName]) => {
                if (
                    Object.keys(appsMap)?.length > 0 &&
                    !appsMap?.[restApiName] &&
                    restApiName?.length > 0 &&
                    !packageRestApiName &&
                    !bridgeRestApiName
                ) {
                    this.store$.dispatch(GetAppDetailsThroughApi({ apiName: restApiName }));
                    setTimeout(() => {
                        if (_action?.type === SetEninvoiceSelectedAppDetails.type) {
                            this.store$.dispatch(SetEninvoiceSelectedAppDetails(_action));
                        } else if (_action?.type === SetEninvoiceSelectedInstanceId.type) {
                            this.store$.dispatch(SetEninvoiceSelectedAppDetails(_action as any));
                        }
                    }, 100);
                    return [];
                }
                return of(GetEninvoiceAppMetaData());
            })
        );
    });

    getEninvoiceAppMetaData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceAppMetaData),
            withLatestFrom(
                this.store$.select(getSubscribedApps$),
                this.store$.select(getEninvoiceSelectedPackageApp$),
                this.store$.select(getEninvoiceSelectedBridgeApp$),
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getEninvoiceSelectedInstanceId$),
                this.store$.select(getEninvoiceSelectedInstance$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([_action, apps, packageApp, bridge, app, _instanceId, instance, version]) => {
                if (!apps || apps.length === 0) {
                    setTimeout(() => {
                        this.store$.dispatch(GetEninvoiceAppMetaData());
                    }, 250);
                    return [];
                }
                if (packageApp?.assetType === 'PACKAGE' && !app?.restApiName) {
                    setTimeout(() => {
                        this.store$.dispatch(GetEninvoiceAppMetaData());
                    }, 250);
                    return [];
                }
                if (bridge?.assetType === 'BRIDGE_ASSET' && !app?.restApiName) {
                    setTimeout(() => {
                        this.store$.dispatch(GetEninvoiceAppMetaData());
                    }, 250);
                    return of(
                        GetAppBridgeNodes({
                            serviceId: bridge?.serviceId,
                        })
                    );
                }
                return of(
                    GetAppMetaData({
                        serviceId: app?.serviceId,
                        assetId: instance?.assetId || app?.assetMetaUId || version?.[app?.serviceId]?.metadataId,
                    })
                );
            })
        );
    });

    getEninvoiceAppMetaDataOnInstanceLoad$ = createEffect(() => {
        return this.store$.select(getEninvoiceSelectedInstance$).pipe(
            distinctUntilChanged((previous, current) => previous?.assetId === current?.assetId),
            filter(instance => !!instance?.assetId),
            map(() => GetEninvoiceAppMetaData())
        );
    });

    clearEninvoiceInstancesPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearEninvoiceInstancesPagination),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([_action, app, organizationId, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: app?.serviceId }));
                    setTimeout(() => {
                        this.store$.dispatch(ClearEninvoiceInstancesPagination());
                    }, 500);
                    return [];
                }
                this.store$.dispatch(
                    ClearInstancesPagination({
                        serviceId: app?.serviceId,
                        organizationId,
                    })
                );
                this.store$.dispatch(
                    ClearSearchInstances({
                        serviceId: app.serviceId,
                        criteriaName: 'eninvoice',
                        organizationId,
                    })
                );
                return [];
            })
        );
    });

    setEninvoiceInstancesPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetEninvoiceInstancesPagination),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getEninvoiceInstancesPagination$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, app, organizationId, pagination, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: app?.serviceId }));
                    setTimeout(() => {
                        this.store$.dispatch(SetEninvoiceInstancesPagination(action));
                    }, 500);
                    return [];
                }
                if (!!pagination.size && pagination.size !== action.size) {
                    this.store$.dispatch(
                        ClearInstancesPagination({
                            serviceId: app?.serviceId,
                            organizationId,
                        })
                    );
                    this.store$.dispatch(
                        ClearSearchInstances({
                            serviceId: app.serviceId,
                            criteriaName: 'eninvoice',
                            organizationId,
                        })
                    );
                }
                setTimeout(() => {
                    this.store$.dispatch(
                        SetInstancesPagination({
                            serviceId: app?.serviceId,
                            organizationId,
                            ...action,
                        })
                    );
                }, 10);
                return [];
            })
        );
    });

    setEninvoiceInstancesSearchCriteria = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetEninvoiceInstancesSearchCriteria),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getEninvoiceAppMetaData$),
                this.store$.select(getEninvoiceSelectedRequestId$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, app, metaData, requestId, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: app?.serviceId }));
                    setTimeout(() => {
                        this.store$.dispatch(SetEninvoiceInstancesSearchCriteria(action));
                    }, 500);
                    return [];
                }
                if (!metaData?.restAPIName) {
                    return [];
                }
                if (action.criteria) {
                    return of(
                        SetSearchInstancesCriteria({
                            serviceId: app?.serviceId,
                            criteriaName: 'eninvoice',
                            searchCriteria: action.criteria,
                        })
                    );
                }
                const primaryEntity = metaData?.getPrimaryEntity();
                const searchText = action.freeTextSearch;
                const rules = [
                    {
                        operator: 'IN',
                        fieldId: 'instanceState.raw',
                        restrictionType: 'SingleRestrictionMetadata',
                        value: ['IN_PROGRESS', 'REQUEST_COMPLETED'],
                    },
                ];
                const searchableFields = primaryEntity?.fields?.filter(field => field.searchable && field.show);
                if (searchText?.length > 0 && searchableFields?.length > 0) {
                    const subRules = [];
                    searchableFields.forEach(field => {
                        subRules.push({
                            fieldId: field?.uid,
                            operator: 'CONTAINS',
                            restrictionType: 'SingleRestrictionMetadata',
                            value: `*${action.freeTextSearch}*`,
                        });
                    });
                    rules.push({
                        condition: 'OR',
                        restrictionType: 'JoinedRestrictionMetadata',
                        rules: subRules,
                    } as any);
                }
                if (requestId?.length > 0) {
                    rules.push({
                        fieldId: 'requestId',
                        operator: 'EQ',
                        restrictionType: 'SingleRestrictionMetadata',
                        value: requestId as any,
                    });
                }
                return of(
                    SetSearchInstancesCriteria({
                        serviceId: app?.serviceId,
                        criteriaName: 'eninvoice',
                        searchCriteria: {
                            entityId: primaryEntity?.uid,
                            restApiServiceName: app?.restApiName,
                            sort: {},
                            filterCriteria: {
                                entityFilterCriterias: [
                                    {
                                        entityUid: primaryEntity?.uid,
                                        criteriaDefinitions: [
                                            {
                                                type: 'CriteriaDefinition',
                                                criteriaName: primaryEntity?.uid,
                                                restriction: {
                                                    condition: 'AND',
                                                    rules,
                                                    restrictionType: 'JoinedRestrictionMetadata',
                                                },
                                            },
                                        ],
                                    },
                                ],
                                criteriaRelations: {
                                    condition: 'AND',
                                    rules: [
                                        {
                                            operator: 'EQ',
                                            value: primaryEntity?.uid,
                                            fieldId: 'Criteria',
                                            restrictionType: 'SingleRestrictionMetadata',
                                        },
                                    ],
                                    restrictionType: 'JoinedRestrictionMetadata',
                                },
                            },
                            size: 100,
                            loadRequests: true,
                        },
                    })
                );
            })
        );
    });

    getEninvoiceSelectedInstanceReports = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceSelectedInstanceReports),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$), this.store$.select(getEninvoiceSelectedInstance$)),
            mergeMap(([_action, app, instance]) => {
                return of(
                    GetReports({
                        instanceId: instance.assetDataId,
                        serviceId: app?.serviceId,
                        assetId: instance.assetId,
                        noAlerts: _action.noAlerts,
                        forceFetch: _action.forceFetch,
                    })
                );
            })
        );
    });

    clearEninvoiceSelectedInstanceReports = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearEninvoiceSelectedInstanceReports),
            withLatestFrom(this.store$.select(getEninvoiceSelectedInstance$)),
            mergeMap(([_action, instance]) => {
                return of(SetReports({ instanceId: instance.assetDataId, reports: [] }));
            })
        );
    });

    eninvoiceSelectedInstanceGenerateReport = createEffect(() => {
        return this.actions$.pipe(
            ofType(EninvoiceSelectedInstanceGenerateReport),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$), this.store$.select(getEninvoiceSelectedInstance$)),
            mergeMap(([_action, app, instance]) => {
                return of(
                    GenerateReport({
                        instanceId: instance.assetDataId,
                        serviceId: app?.serviceId,
                        chainName: _action.chainName,
                        repositoryId: _action.repositoryId,
                        transformationName: undefined,
                        noAlerts: _action.noAlerts,
                        timeStamp: CommonUtilsService.getOrgAndUserOffsetTimeStamp(),
                    })
                );
            })
        );
    });

    searchInstances = createEffect(() => {
        return this.actions$.pipe(
            ofType(SearchEninvoiceInstances),
            delay(10),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getEninvoiceAppMetaData$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$),
                this.store$.select(getCurrentOrganizationId$)
            ),
            mergeMap(([_action, app, metaData, organizationId, versionsMap, currentOrganizationId]) => {
                if (!location.href?.includes(currentOrganizationId)) {
                    setTimeout(() => {
                        this.store$.dispatch(SearchEninvoiceInstances(_action));
                    }, 500);
                    return [];
                }
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: app?.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(SearchEninvoiceInstances(_action));
                    }, 500);
                    return [];
                }
                if (!metaData?.restAPIName) {
                    return [];
                }
                return of(
                    SearchInstances({
                        criteriaName: 'eninvoice',
                        organizationId,
                        serviceId: app?.serviceId,
                        isBackgroundProcess: false,
                        noAlerts: false,
                        forceFetch: _action.forceFetch,
                    })
                );
            })
        );
    });

    getEninvoiceSearchReportsPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceSearchReportsPagination),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    GetSearchReportsPagination({
                        serviceId: app?.serviceId,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    updateEninvoiceSearchReportsPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateEninvoiceSearchReportsPagination),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    UpdateSearchReportsPagination({
                        serviceId: app?.serviceId,
                        pageSize: _action.pageSize,
                        pageIndex: _action.pageIndex,
                        pagingState: _action.pagingState,
                        timeRange: _action.timeRange,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    clearEninvoiceSearchReportsPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearEninvoiceSearchReportsPagination),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    ClearSearchReportsPagination({
                        serviceId: app?.serviceId,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    getEninvoiceSearchReports = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceSearchReports),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    GetSearchReports({
                        serviceId: app?.serviceId,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    clearEninvoiceSearchReports = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearEninvoiceSearchReports),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    ClearSearchReports({
                        serviceId: app?.serviceId,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    getEninvoiceTransformationChains = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceTransformationChains),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    GetOutboundChains({
                        serviceId: app?.serviceId,
                        restApiName: app?.restApiName,
                    })
                );
            })
        );
    });

    downloadEninvoiceSearchReport = createEffect(() => {
        return this.actions$.pipe(
            ofType(DownloadEninvoiceSearchReport),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$), this.store$.select(getAssetsWithLatestVersion$)),
            mergeMap(([_action, app, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: app?.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(DownloadEninvoiceSearchReport(_action));
                    }, 500);
                    return [];
                }
                return of(
                    DownloadSearchReport({
                        serviceId: app?.serviceId,
                        assetId: app?.assetMetaUId || versionsMap?.[app?.serviceId]?.metadataId,
                        requestId: _action.requestId,
                        repositoryId: _action.repositoryId,
                        transformationName: _action.transformationName,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    GetEninvoiceSelectedInstanceWorkflow = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceSelectedInstanceWorkflow),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$), this.store$.select(getEninvoiceSelectedInstanceId$)),
            mergeMap(([_action, app, instanceId]) => {
                if (!instanceId) {
                    return [];
                }
                return of(
                    GetWorkflow({
                        instanceId: instanceId,
                        serviceId: app?.serviceId,
                        noAlerts: _action.noAlerts,
                        restApiName: app?.restApiName,
                        notFirstIteration: _action?.notFirstIteration,
                        configurations: _action?.configurations,
                    })
                );
            })
        );
    });

    ClearEninvoiceSelectedInstanceWorkflow = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearEninvoiceSelectedInstanceWorkflow),
            withLatestFrom(this.store$.select(getEninvoiceSelectedInstance$)),
            mergeMap(([_action, instance]) => {
                if (!instance?.assetDataId) {
                    return [];
                }
                return of(
                    SetWorkflow({
                        instanceId: instance.assetDataId,
                        workflows: undefined,
                    })
                );
            })
        );
    });

    EninvoiceSubmitWorkflow = createEffect(() => {
        return this.actions$.pipe(
            ofType(SubmitEninvoiceWorkflow),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$), this.store$.select(getEninvoiceSelectedInstance$)),
            mergeMap(([_action, app, instance]) => {
                if (!instance?.assetDataId) {
                    return [];
                }
                return of(
                    ExecuteWorkflowStage({
                        apiAction: _action.action.name,
                        instanceIds: [instance.assetDataId],
                        comment: _action.comment,
                        formFields: _action.formFields,
                        serviceId: app?.serviceId,
                        executeTimestamp: _action.executeTimestamp,
                        executeWithLatestEvent: true,
                    })
                );
            })
        );
    });

    getEninvoiceInstance = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceInstance),
            withLatestFrom(this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([action, app]) => {
                return of(
                    GetInstance({
                        serviceId: app?.serviceId,
                        instanceId: action.instanceId,
                        noAlerts: false,
                    })
                );
            })
        );
    });

    SetEninvoiceRequestsPagination = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetEninvoiceRequestsPagination),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getEninvoiceRequestsPagination$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, app, organizationId, pagination, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: app?.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(SetEninvoiceRequestsPagination(action));
                    }, 500);
                    return [];
                }
                if (!!pagination.pageSize && pagination.pageSize !== action.pageSize) {
                    this.store$.dispatch(
                        ClearRequestsPagination({
                            serviceId: app?.serviceId,
                            organizationId,
                        })
                    );
                }
                setTimeout(() => {
                    this.store$.dispatch(
                        SetRequestsPagination({
                            serviceId: app?.serviceId,
                            organizationId,
                            ...action,
                        })
                    );
                }, 10);
                return [];
            })
        );
    });

    GetEninvoiceRequests = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceRequests),
            delay(10),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getEninvoiceAppMetaData$),
                this.store$.select(getAssetsWithLatestVersion$),
                this.store$.select(getSelectedRequestLogName$)
            ),
            mergeMap(([_action, app, metaData, versionsMap, selectedRequestLogName]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: app?.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetEninvoiceRequests(_action));
                    }, 500);
                    return [];
                }
                if (!metaData?.restAPIName) {
                    return [];
                }
                if (_action.search) {
                } else {
                    return of(
                        GetRequests({
                            criteriaName: selectedRequestLogName,
                            serviceId: app?.serviceId,
                            restApiName: metaData?.restAPIName,
                            assetName: app?.serviceId ? app.serviceName : '-',
                            status: _action.status,
                            noAlerts: false,
                            forceFetch: true,
                        })
                    );
                }
            })
        );
    });

    GetEninvoiceRequestsByInstanceId = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoiceRequestsByInstanceId),
            debounceTime(10),
            withLatestFrom(
                this.store$.select(getEninvoiceSelectedApp$),
                this.store$.select(getEninvoiceAppMetaData$),
                this.store$.select(getSelectedRequestLogName$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, app, metaData, logName, versionsMap]) => {
                if (!versionsMap?.[app?.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: app?.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetEninvoiceRequestsByInstanceId(action));
                    }, 500);
                    return [];
                }
                if (!metaData?.restAPIName) {
                    return [];
                }
                return of(
                    GetRequestsByInstanceId({
                        restApiName: metaData?.restAPIName,
                        serviceId: app?.serviceId,
                        assetDataIds: action.assetDataIds,
                        criteriaName: logName || 'eninvoice',
                        noAlerts: false,
                    })
                );
            })
        );
    });

    ClearEninvoiceRequests = createEffect(() =>
        this.actions$.pipe(
            ofType(ClearEninvoiceRequests),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getEninvoiceSelectedApp$)),
            mergeMap(([_action, organizationId, app]) => {
                return of(
                    ClearRequests({
                        organizationId,
                        serviceId: app?.serviceId,
                        criteria: _action.criteria || 'eninvoice',
                    })
                );
            })
        )
    );

    GetEninvoicePackageApps = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEninvoicePackageApps),
            withLatestFrom(this.store$.select(getEninvoiceSelectedPackageApp$)),
            mergeMap(([_action, app]) => {
                return of(
                    GetPackageApps({
                        serviceId: app?.serviceId,
                    })
                );
            })
        );
    });
}
